import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Server, Mail, User, Key, Hash } from 'lucide-react';
import { API_BASE_URL } from '../api/config';
import { sessionManager } from '../api/sessionManager';

export default function SMTPSettingsModal({ onClose }) {
  const [formData, setFormData] = useState({
    mail_from: '',
    mail_host: 'smtp.gmail.com',
    mail_port: '587',
    mail_username: '',
    mail_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const user = sessionManager.getUser();
  const userId = user?.id;
  const role = user?.role || 'employee';

  useEffect(() => {
    const fetchSettings = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/smtp_settings.php?user_id=${userId}&role=${role}`);
        const data = await res.json();
        if (data.success && data.data) {
          setFormData({
            mail_from: data.data.mail_from || '',
            mail_host: data.data.mail_host || 'smtp.gmail.com',
            mail_port: data.data.mail_port || '587',
            mail_username: data.data.mail_username || '',
            mail_password: '' // Don't populate password for security
          });
        }
      } catch (err) {
        console.error('Failed to fetch SMTP settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [userId, role]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!userId) {
      showToast('Session expired. Please log in again.', 'error');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        user_id: userId,
        role: role
      };
      
      const res = await fetch(`${API_BASE_URL}/smtp_settings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (data.success) {
        showToast('SMTP Settings saved successfully!', 'success');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showToast(data.message || 'Failed to save settings', 'error');
      }
    } catch (err) {
      showToast('Network error while saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete your SMTP settings?")) return;
    if (!userId) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/smtp_settings.php`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role })
      });
      const data = await res.json();
      
      if (data.success) {
        showToast('SMTP Settings deleted successfully!', 'success');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showToast(data.message || 'Failed to delete settings', 'error');
      }
    } catch (err) {
      showToast('Network error while deleting settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      
      {toast && (
        <div className={`fixed top-10 left-1/2 transform -translate-x-1/2 z-[300] max-w-sm w-11/12 px-4 py-3 rounded-xl shadow-2xl flex items-center justify-between gap-3 text-[13px] font-medium border animate-in slide-in-from-top-4 duration-300 ${
          toast.type === 'success' 
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
            : 'bg-rose-50 border-rose-200 text-rose-800'
        }`}>
          <span className="flex-1">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} className="opacity-60 hover:opacity-100 transition-opacity">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Server size={18} className="text-gray-300" />
            <h3 className="font-semibold">SMTP Configuration</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSave} autoComplete="off" className="p-6 space-y-4">
          {/* Hidden inputs to trick browser autofill */}
          <input type="text" style={{display: 'none'}} />
          <input type="password" style={{display: 'none'}} />

          {loading ? (
            <div className="py-10 flex justify-center text-gray-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail size={12} className="text-gray-400" /> From Email Address *
                </label>
                <input 
                  type="email" name="mail_from" required autoComplete="off"
                  value={formData.mail_from} onChange={handleChange}
                  placeholder="noreply@bankingcrm.com"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Server size={12} className="text-gray-400" /> SMTP Host *
                </label>
                <input 
                  type="text" name="mail_host" required autoComplete="off"
                  value={formData.mail_host} onChange={handleChange}
                  placeholder="smtp.gmail.com"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Hash size={12} className="text-gray-400" /> SMTP Port *
                </label>
                <input 
                  type="number" name="mail_port" required autoComplete="off"
                  value={formData.mail_port} onChange={handleChange}
                  placeholder="587"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <User size={12} className="text-gray-400" /> SMTP Username *
                </label>
                <input 
                  type="text" name="mail_username" required autoComplete="new-password"
                  value={formData.mail_username} onChange={handleChange}
                  placeholder="Your SMTP Username"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Key size={12} className="text-gray-400" /> SMTP Password
                </label>
                <input 
                  type="password" name="mail_password" autoComplete="new-password"
                  value={formData.mail_password} onChange={handleChange}
                  placeholder={formData.mail_username ? "Leave blank to keep existing password" : "Your SMTP Password"}
                  required={!formData.mail_username} // Required only if no existing settings
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
              </div>
            </>
          )}

          {/* Footer */}
          <div className="pt-4 border-t border-gray-100 flex justify-between items-center mt-6">
            <button 
              type="button" 
              onClick={handleDelete}
              disabled={loading || saving || !formData.mail_username}
              className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30"
            >
              Delete Config
            </button>
            <div className="flex items-center gap-2">
              <button 
                type="button" 
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading || saving}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save Settings
              </button>
            </div>
          </div>
        </form>

      </div>
    </div>
  );
}
